import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  RowActionMenu,
  type RowAction,
} from '@backrs_ui/grow-ui/src/components/RowActionMenu/RowActionMenu';
import {
  cohortLabel,
  institutionLabel,
  offeringLabel,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import {
  isCoachableRoster,
  useGetRosterCodesQuery,
  type AdminCode,
  type AdminCohort,
  type AdminRoster,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { CohortCoachesCell } from './CohortCoachesCell';
import { LineCode, lineCodeState } from './LineCode';
import { activeCodeForLine, cohortLineKey } from './rosterCodes';

const strings = appStrings.modules.rostersAdmin;
const { cohorts: cohortStrings } = appStrings.modules.coachingDashboard.institutionAdmin;

const nameCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  '& .cohort-rename': { opacity: 0, transition: 'opacity 0.15s' },
  '&:hover .cohort-rename, & .cohort-rename:focus-visible': { opacity: 1 },
});

export type CohortDoors = Readonly<{
  /** True only for the line whose own mint is in flight; one mutation instance serves them all. */
  isCreatingFor: (lineKey: string) => boolean;
  onCreateCode: (roster: AdminRoster, cohort: AdminCohort) => void;
  onManageCoaches: (roster: AdminRoster, cohort: AdminCohort) => void;
  onAddMembers: (roster: AdminRoster, cohort: AdminCohort) => void;
  onRemoveMembers: (roster: AdminRoster, cohort: AdminCohort) => void;
  onRename: (roster: AdminRoster, cohort: AdminCohort) => void;
  onRevoke: (roster: AdminRoster, cohort: AdminCohort, code: AdminCode) => void;
}>;

type CohortRowProps = Readonly<{
  roster: AdminRoster;
  cohort: AdminCohort;
  doors: CohortDoors;
}>;

/**
 * The line's code comes from the codes read rather than from the cohort row's own share_code,
 * so the value shown and the value the revoke door acts on are the same fact.
 */
export const CohortRow = ({ roster, cohort, doors }: CohortRowProps) => {
  const codes = useGetRosterCodesQuery({ rosterId: roster.id });
  const activeCode = activeCodeForLine(codes.data?.data ?? [], cohort.id);
  const codeState = lineCodeState(
    activeCode,
    codes.isFetching,
    codes.isError,
    // A partial decode failure is not an error on this read — contextualList only errors when
    // EVERY row fails — so completeness has to travel separately from isError.
    (codes.data?.dropped ?? 0) === 0,
  );
  const lineKey = cohortLineKey(cohort.id);

  const isCreating = doors.isCreatingFor(lineKey);
  const isCoachable = isCoachableRoster(roster);

  const actions: RowAction[] = [
    {
      id: `rosters-admin-cohort-manage-coaches-${cohort.id}`,
      label: strings.actions.manageCoaches,
      // C2c: an attachment on a line nobody can be coached from is accepted and confers
      // nothing, so the entry says why rather than opening onto a write that does nothing.
      disabled: !isCoachable,
      disabledReason: strings.actions.notCoachable,
      onSelect: () => doors.onManageCoaches(roster, cohort),
    },
    {
      id: `rosters-admin-cohort-add-members-${cohort.id}`,
      label: strings.actions.addMembers,
      onSelect: () => doors.onAddMembers(roster, cohort),
    },
    {
      id: `rosters-admin-cohort-remove-members-${cohort.id}`,
      label: strings.actions.removeMembers,
      onSelect: () => doors.onRemoveMembers(roster, cohort),
    },
  ];

  if (activeCode !== null && codeState === 'present') {
    const code = activeCode;

    actions.push({
      id: `rosters-admin-cohort-revoke-${cohort.id}`,
      label: strings.actions.revoke,
      onSelect: () => doors.onRevoke(roster, cohort, code),
    });
  }

  return (
    <TableRow id={`dashboard-cohort-row-${cohort.id}`} data-line={lineKey}>
      <TableCell>
        <Box sx={nameCellSx}>
          <Typography variant="text-sm-medium">{cohortLabel(cohort)}</Typography>
          <IconButton
            id={`rosters-admin-cohort-rename-${cohort.id}`}
            className="cohort-rename"
            size="small"
            aria-label={strings.actions.rename}
            onClick={() => doors.onRename(roster, cohort)}
          >
            <AppIcon iconName="Edit02" diameter="16px" color="Colors/Foreground/fg-secondary" />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">{offeringLabel(roster.offering)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">
          {institutionLabel(roster.managingInstitution)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="text-sm-regular">{cohort.memberCount}</Typography>
      </TableCell>
      <TableCell>
        <CohortCoachesCell
          id={`rosters-admin-cohort-coaches-${cohort.id}`}
          coaches={cohort.coaches}
        />
      </TableCell>
      <TableCell>
        <LineCode
          id={`dashboard-cohort-share-code-${cohort.id}`}
          code={activeCode}
          state={codeState}
          isCreating={isCreating}
          onCreate={() => doors.onCreateCode(roster, cohort)}
        />
      </TableCell>
      <TableCell>
        <RowActionMenu
          id={`rosters-admin-cohort-actions-${cohort.id}`}
          ariaLabel={strings.actions.cohortAria(cohortLabel(cohort))}
          actions={actions}
        />
      </TableCell>
    </TableRow>
  );
};

export const COHORT_TABLE_ARIA = cohortStrings.title;

export const COHORT_HEADERS = [
  cohortStrings.columns.cohort,
  strings.columnsV2.program,
  strings.columnsV2.institution,
  cohortStrings.columns.members,
  cohortStrings.columns.coaches,
  strings.columnsV2.code,
  strings.columns.actions,
];
